import type { EmployeeRecordT, IdentificationT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { identificationKeys } from '@/entities/identification';
import { meKeys } from '@/entities/me';
import { queryClient } from '@/app/providers/queryClient';

import type { AnalyticsContext, AnalyticsContextInput } from '../events.js';

import { getWorkspaceSessionId } from './workspaceSession.js';

function toScopeFields(
  scope: AnalyticsContextInput['scope']
): Pick<AnalyticsContext, 'scopeType' | 'scopeId'> {
  if (!scope) {
    return {};
  }

  switch (scope.type) {
    case 'patient':
      return { scopeType: 'patient' };
    case 'problem':
      return { scopeType: 'problem', scopeId: scope.id };
    case 'request':
      return { scopeType: 'request', scopeId: scope.id };
    default: {
      const _exhaustive: never = scope;
      return _exhaustive;
    }
  }
}

export function buildAnalyticsContext(input: AnalyticsContextInput): AnalyticsContext {
  const identification = queryClient.getQueryData<IdentificationT>(identificationKeys.my());
  const clinicianUserId = identification?.userId;

  const employeeRecord = queryClient.getQueryData<EmployeeRecordT>(meKeys.employeeRecord());
  const clinicianDisplayName = employeeRecord?.displayName ?? undefined;

  const patientUserId =
    input.patientUserId === undefined ? undefined : input.patientUserId.trim() || undefined;

  const workspaceSessionId =
    patientUserId === undefined ? undefined : getWorkspaceSessionId(patientUserId);

  return {
    actor: input.actor,
    clinicianUserId,
    clinicianDisplayName,
    patientUserId,
    workspaceSessionId,
    ...toScopeFields(input.scope),
  };
}
